import { useState, useMemo } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Phone, Clock, User, Calendar as CalendarIcon } from "lucide-react";
import { format, isSameDay, parseISO } from "date-fns";
import { Call } from "@/hooks/useCalls";
import { cn } from "@/lib/utils";
import { ScheduleCallDialog } from "./ScheduleCallDialog";

interface CallsCalendarProps {
  calls: Call[];
  onCallSelect?: (call: Call) => void;
}

export function CallsCalendar({ calls, onCallSelect }: CallsCalendarProps) {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());

  // Get calls with scheduled_at dates
  const scheduledCalls = useMemo(() => {
    return calls.filter((call) => call.scheduled_at || call.started_at);
  }, [calls]);

  // Get calls for selected date
  const callsForSelectedDate = useMemo(() => {
    if (!selectedDate) return [];
    return scheduledCalls.filter((call) => {
      const callDate = call.scheduled_at || call.started_at;
      if (!callDate) return false;
      return isSameDay(parseISO(callDate), selectedDate);
    });
  }, [scheduledCalls, selectedDate]);

  // Get dates that have calls
  const datesWithCalls = useMemo(() => {
    const dates = new Set<string>();
    scheduledCalls.forEach((call) => {
      const callDate = call.scheduled_at || call.started_at;
      if (callDate) {
        dates.add(format(parseISO(callDate), "yyyy-MM-dd"));
      }
    });
    return dates;
  }, [scheduledCalls]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "scheduled":
        return "bg-blue-500/10 text-blue-500 border-blue-500/20";
      case "completed":
        return "bg-green-500/10 text-green-500 border-green-500/20";
      case "in_progress":
        return "bg-yellow-500/10 text-yellow-500 border-yellow-500/20";
      case "missed":
        return "bg-red-500/10 text-red-500 border-red-500/20";
      case "cancelled":
        return "bg-muted text-muted-foreground border-muted";
      default:
        return "bg-muted text-muted-foreground border-muted";
    }
  };

  const formatDuration = (seconds: number) => {
    if (!seconds) return "—";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Calendar */}
      <Card className="glass">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarIcon className="h-5 w-5 text-primary" />
            Call Calendar
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={setSelectedDate}
            className="rounded-md border-0"
            modifiers={{
              hasCalls: (date) =>
                datesWithCalls.has(format(date, "yyyy-MM-dd")),
            }}
            modifiersClassNames={{
              hasCalls: "bg-primary/20 font-bold",
            }}
          />
          <div className="mt-4 flex items-center gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-primary/20" />
              <span>Has calls</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Calls for selected date */}
      <Card className="glass">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>
              {selectedDate
                ? format(selectedDate, "MMMM d, yyyy")
                : "Select a date"}
            </span>
            <div className="flex items-center gap-2">
              <Badge variant="secondary">
                {callsForSelectedDate.length} call{callsForSelectedDate.length !== 1 ? "s" : ""}
              </Badge>
              <ScheduleCallDialog selectedDate={selectedDate} />
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[400px] pr-4">
            {callsForSelectedDate.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                <Phone className="h-12 w-12 mb-4 opacity-50" />
                <p>No calls scheduled for this date</p>
              </div>
            ) : (
              <div className="space-y-3">
                {callsForSelectedDate.map((call) => {
                  const callTime = call.scheduled_at || call.started_at;
                  const contactName = call.contacts
                    ? `${call.contacts.first_name} ${call.contacts.last_name}`
                    : call.leads
                    ? `${call.leads.first_name || ""} ${call.leads.last_name || ""}`.trim() || call.leads.company_name
                    : "Unknown";

                  return (
                    <div
                      key={call.id}
                      onClick={() => onCallSelect?.(call)}
                      className={cn(
                        "p-4 rounded-lg border bg-card hover:bg-accent/50 cursor-pointer transition-colors",
                        "flex flex-col gap-2"
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-primary" />
                          <span className="font-medium">{contactName}</span>
                        </div>
                        <Badge className={getStatusColor(call.status)}>
                          {call.status}
                        </Badge>
                      </div>

                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        {callTime && (
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {format(parseISO(callTime), "h:mm a")}
                          </div>
                        )}
                        {call.duration_seconds > 0 && (
                          <div className="flex items-center gap-1">
                            <span>Duration:</span>
                            {formatDuration(call.duration_seconds)}
                          </div>
                        )}
                        <div className="flex items-center gap-1">
                          <Badge variant="outline" className="text-xs">
                            {call.direction}
                          </Badge>
                        </div>
                      </div>

                      {call.deals?.title && (
                        <div className="text-sm text-muted-foreground">
                          Deal: {call.deals.title}
                        </div>
                      )}

                      {call.notes && (
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {call.notes}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}
